import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Edit3, Check, Upload, Image as ImageIcon, ArrowUpDown, Eye, EyeOff, AlertTriangle } from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { SortableList } from "../../components/SortableList";
import { toast } from "sonner";
import EmptyState, { LoadingState } from "../../components/EmptyState";

const BLANK = {
  title: "", subtitle: "", image_url: "",
  cta_label: "Shop now", cta_link: "/app/products",
  background_color: "#3A86FF", text_color: "#FFFFFF",
  display_order: 0,
};

export default function AdminBanners() {
  const { tenant } = useAuth();
  const [banners, setBanners] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const load = async () => {
    setLoadError(null);
    try {
      const { data } = await api.get("/api/admin/banners");
      setBanners(data.banners);
    } catch (e) {
      setLoadError(friendlyMessage(e));
      toast.error(friendlyMessage(e));
    }
  };
  useEffect(() => { load().finally(() => setLoading(false)); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/api/admin/banners/${editing}`, form);
        toast.success("Banner updated");
      } else {
        await api.post("/api/admin/banners", form);
        toast.success("Banner added");
      }
      setOpen(false); setEditing(null); setForm(BLANK);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    await api.delete(`/api/admin/banners/${id}`);
    toast.success("Deleted"); load();
  };

  const toggle = async (b) => {
    await api.patch(`/api/admin/banners/${b.id}`, { is_active: !b.is_active });
    load();
  };

  const startEdit = (b) => {
    setEditing(b.id);
    setForm({
      title: b.title || "", subtitle: b.subtitle || "",
      image_url: b.image_url || "",
      cta_label: b.cta_label || "Shop now",
      cta_link: b.cta_link || "/app/products",
      background_color: b.background_color || "#3A86FF",
      text_color: b.text_color || "#FFFFFF",
      display_order: b.display_order || 0,
    });
    setOpen(true);
  };

  const reorder = async (next) => {
    setBanners(next);
    try {
      await api.post("/api/admin/banners/reorder", { ids: next.map((b) => b.id) });
      toast.success("Order saved");
    } catch {
      toast.error("Reorder failed"); load();
    }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("folder", "banners");
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const primary = tenant?.branding?.primary_color || "#3A86FF";

  if (loading) return <LoadingState fullHeight />;
  if (loadError) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load banners" description={loadError}
        action={<button onClick={() => { setLoading(true); load().finally(() => setLoading(false)); }} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }

  return (
    <div className="space-y-6 page-enter" data-testid="admin-banners-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Storefront</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Banners ({banners.length})</h1>
          <p className="text-sm text-neutral-600 mt-1">Promote seasonal offers above the category strip on your customer home.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ ...BLANK, background_color: primary, display_order: banners.length + 1 }); setOpen(true); }}
                className="h-11 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full sm:w-auto"
                data-testid="add-banner-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Banner
        </Button>
      </div>

      {banners.length === 0 ? (
        <div className="rounded-3xl bg-white border border-dashed border-neutral-300" data-testid="empty-banners">
          <EmptyState
            icon={ImageIcon}
            size="lg"
            title="No banners yet"
            description="Add your first promo banner to greet visitors with seasonal offers."
          />
        </div>
      ) : (
        <SortableList
          items={banners}
          onReorder={reorder}
          keyOf={(b) => b.id}
          className="space-y-3"
          testIdPrefix="banner-sort"
          renderItem={(b, handle) => (
            <div className={`bg-white rounded-3xl border ${b.is_active ? "border-neutral-200" : "border-dashed border-neutral-300 opacity-60"} overflow-hidden`} data-testid={`banner-${b.id}`}>
              <div className="flex flex-col sm:flex-row">
                <div className="sm:w-64 h-32 sm:h-auto shrink-0 relative overflow-hidden"
                     style={{ background: b.background_color || "#3A86FF" }}>
                  {b.image_url ? (
                    <img src={b.image_url} alt={b.title} className="w-full h-full object-cover"/>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-heading font-extrabold text-2xl"
                         style={{ color: b.text_color || "#fff" }}>
                      {b.title?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-white/90 text-[10px] font-bold uppercase tracking-wider text-neutral-700">
                    <ArrowUpDown className="w-3 h-3 inline mr-1"/>#{b.display_order}
                  </span>
                </div>
                <div className="flex-1 p-5 flex flex-col">
                  <div className="flex items-start gap-2">
                    {handle}
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-extrabold text-lg">{b.title}</div>
                      <div className="text-sm text-neutral-600 mt-1">{b.subtitle}</div>
                    </div>
                  </div>
                  <div className="text-xs text-neutral-500 font-mono mt-2 flex items-center gap-3 flex-wrap">
                    <span><b className="text-neutral-700">CTA:</b> {b.cta_label}</span>
                    <span className="truncate">→ {b.cta_link}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-4 flex-wrap">
                    <button onClick={() => startEdit(b)} className="text-sm text-brand-sky font-bold inline-flex items-center gap-1" data-testid={`edit-banner-${b.id}`}>
                      <Edit3 className="w-3.5 h-3.5"/> Edit
                    </button>
                    <button onClick={() => toggle(b)} className="text-sm text-neutral-600 font-bold inline-flex items-center gap-1" data-testid={`toggle-banner-${b.id}`}>
                      {b.is_active ? <><EyeOff className="w-3.5 h-3.5"/> Hide</> : <><Eye className="w-3.5 h-3.5"/> Show</>}
                    </button>
                    <button onClick={() => del(b.id)} className="text-sm text-brand-clay font-bold inline-flex items-center gap-1 ml-auto" data-testid={`delete-banner-${b.id}`}>
                      <Trash2 className="w-3.5 h-3.5"/> Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        />
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(BLANK); } }}>
        <DialogContent className="max-w-xl rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit banner" : "New banner"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Preview */}
            <div className="rounded-2xl overflow-hidden h-32 relative" style={{ background: form.background_color || primary }}>
              {form.image_url && <img src={form.image_url} alt="" className="absolute inset-0 w-full h-full object-cover"/>}
              <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"/>
              <div className="relative h-full p-4 flex flex-col justify-center" style={{ color: form.text_color || "#fff" }}>
                <div className="text-xs font-bold uppercase tracking-wider opacity-90">{form.subtitle || "Subtitle"}</div>
                <div className="font-heading text-2xl font-extrabold leading-tight">{form.title || "Headline"}</div>
                <div className="mt-1.5 text-xs font-bold">{form.cta_label} →</div>
              </div>
            </div>

            <Field label="Headline *">
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                     placeholder="Farm-fresh A2 milk" className="h-12 rounded-2xl" data-testid="banner-title"/>
            </Field>
            <Field label="Subtitle">
              <Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                     placeholder="Subscribe & save 15% this week" className="h-12 rounded-2xl" data-testid="banner-subtitle"/>
            </Field>
            <Field label="Background image (optional)">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-2xl bg-brand-cream border border-neutral-200 flex items-center justify-center overflow-hidden shrink-0">
                  {form.image_url ? (
                    <img src={form.image_url} alt="" className="w-full h-full object-cover" data-testid="banner-image-preview"/>
                  ) : <ImageIcon className="w-6 h-6 text-neutral-400"/>}
                </div>
                <input ref={fileRef} type="file" accept="image/*" hidden
                       onChange={(e) => upload(e.target.files?.[0])} data-testid="banner-file-input"/>
                <Button onClick={() => fileRef.current?.click()} disabled={busy} variant="outline"
                        className="h-12 rounded-2xl border-2 border-brand-ink font-bold flex-1"
                        data-testid="banner-upload-btn">
                  <Upload className="w-4 h-4 mr-2"/> {form.image_url ? "Replace" : "Upload"} image
                </Button>
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="CTA label">
                <Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                       placeholder="Shop now" className="h-12 rounded-2xl" data-testid="banner-cta-label"/>
              </Field>
              <Field label="CTA link">
                <Input value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })}
                       placeholder="/app/products?cat=milk" className="h-12 rounded-2xl font-mono text-sm" data-testid="banner-cta-link"/>
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="BG color">
                <div className="flex items-center gap-1.5">
                  <input type="color" value={form.background_color || "#3A86FF"} onChange={(e) => setForm({ ...form, background_color: e.target.value })}
                         className="h-12 w-14 rounded-xl border border-neutral-200 cursor-pointer shrink-0" data-testid="banner-bg-color"/>
                  <Input value={form.background_color} onChange={(e) => setForm({ ...form, background_color: e.target.value })}
                         className="h-12 rounded-2xl font-mono text-xs"/>
                </div>
              </Field>
              <Field label="Text color">
                <div className="flex items-center gap-1.5">
                  <input type="color" value={form.text_color || "#FFFFFF"} onChange={(e) => setForm({ ...form, text_color: e.target.value })}
                         className="h-12 w-14 rounded-xl border border-neutral-200 cursor-pointer shrink-0" data-testid="banner-text-color"/>
                  <Input value={form.text_color} onChange={(e) => setForm({ ...form, text_color: e.target.value })}
                         className="h-12 rounded-2xl font-mono text-xs"/>
                </div>
              </Field>
              <Field label="Order">
                <Input type="number" value={form.display_order}
                       onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })}
                       className="h-12 rounded-2xl" data-testid="banner-order"/>
              </Field>
            </div>
            <Button onClick={save} disabled={busy} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full mt-2" data-testid="save-banner-btn">
              <Check className="w-4 h-4 mr-2"/> {editing ? "Save changes" : "Create banner"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
